import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthStore } from '@scl/core/domains/auth/services/auth.store';
import { ClerkAuthService } from '@scl/core/domains/auth/services/clerk-auth.service';
import { AccountService } from '@scl/core/services/account.service';
import { firstValueFrom } from 'rxjs';
import { LocalStorageService } from '../../shared-module/services/local-storage.service';
import { AuthService } from '../services/auth.service';
import { RoutingService } from '../services/routing.service';
import { JwtTokenService } from '../services/jwt-token.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  private accessResolutionPromise: Promise<boolean | UrlTree> | null = null;
  private accessResolutionUrl: string | null = null;

  private authStore = inject(AuthStore);
  private authService = inject(AuthService);
  private clerkAuthService = inject(ClerkAuthService);
  private jwtTokenService = inject(JwtTokenService);
  private localStorageService = inject(LocalStorageService);
  private router = inject(Router);
  private routingService = inject(RoutingService);
  private accountService = inject(AccountService);

  async canLoad(route: Route): Promise<boolean | UrlTree> {
    const url = `/${route.path ?? ''}`;
    return this.ensureAccess(url);
  }

  async canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return this.ensureAccess(state.url);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot) {
    return this.canActivate(route, state);
  }

  async ensureAccess(url: string): Promise<boolean | UrlTree> {
    if (this.accessResolutionPromise && this.accessResolutionUrl === url) {
      return this.accessResolutionPromise;
    }

    const pendingResolution = this.resolveAccess(url)
      .finally(() => {
        if (this.accessResolutionPromise === pendingResolution) {
          this.accessResolutionPromise = null;
          this.accessResolutionUrl = null;
        }
      });

    this.accessResolutionPromise = pendingResolution;
    this.accessResolutionUrl = url;

    return pendingResolution;
  }

  private async resolveAccess(url: string): Promise<boolean | UrlTree> {
    try {
      const providerConfig = await this.authStore.ensureProviderConfig();
      if (providerConfig.provider === 'Clerk') {
        return this.checkClerkLogin(url);
      }
    } catch {
      // Fall back to the legacy flow when provider bootstrap is unavailable.
    }

    return this.checkLegacyLogin(url);
  }

  private async checkLegacyLogin(url: string): Promise<boolean> {
    if (this.authService.isAuthenticated) {
      return true;
    }

    const token = this.localStorageService.getToken();
    if (!token) {
      this.routingService.navigateAuthUser();
      return false;
    }

    this.jwtTokenService.accessToken = token;
    return this.validateCurrentUser(url, false);
  }

  private async checkClerkLogin(url: string): Promise<boolean | UrlTree> {
    // Fast path — session already validated during this navigation cycle.
    // Prevents redundant getUserInfo() calls from nested guard invocations
    // (canActivateChild, lazy-module guards) that fire after the initial guard resolves.
    if (this.authService.isAuthenticated && this.accountService.currentUser) {
      const cleanUrl = this.clerkAuthService.cleanHandshakeParams(url);
      return cleanUrl ? this.router.parseUrl(cleanUrl) : true;
    }

    if (!await this.authStore.restoreSession()) {
      return this.createClerkLoginRedirect(url);
    }

    const isAuthenticated = await this.validateCurrentUser(url, this.createClerkLoginRedirect(url));
    if (isAuthenticated !== true) {
      return isAuthenticated;
    }

    const cleanUrl = this.clerkAuthService.cleanHandshakeParams(url);
    return cleanUrl ? this.router.parseUrl(cleanUrl) : true;
  }

  private async validateCurrentUser<TFallback extends boolean | UrlTree>(
    url: string,
    onFailure: TFallback
  ): Promise<true | TFallback> {
    try {
      await firstValueFrom(this.accountService.getUserInfo());
      this.authService.redirectUrl = url;
      this.authService.isAuthenticated = true;
      return true;
    } catch (err) {
      console.error(err);
      this.accountService.currentUser = null;
      this.authService.isAuthenticated = false;
      return onFailure;
    }
  }

  private createClerkLoginRedirect(url: string): UrlTree {
    const redirectUrl = this.clerkAuthService.getSafeRedirectUrl(url);

    return this.router.createUrlTree(['/login'], {
      queryParams: redirectUrl ? { redirect_url: redirectUrl } : {}
    });
  }
}
